import clsx from 'clsx'
import {
  type ChangeEventHandler,
  type ForwardedRef,
  forwardRef,
  type PropsWithChildren,
  type ReactNode,
} from 'react'

import type { PropsWithClassName } from '@fv/client-types'

export interface RadioFieldElement<T extends string> extends HTMLInputElement {
  value: T
}

export type FullRadioFieldOption<T extends string = string> = {
  label: ReactNode
  value: T
  disabled?: boolean
  badge?: ReactNode
}

export type RadioFieldOption<T extends string = string> =
  | T
  | FullRadioFieldOption<T>

type Props<T extends string> = {
  className?: string
  disabled?: boolean
  name: string
  onChange?: ChangeEventHandler<RadioFieldElement<T>>
  options: Array<RadioFieldOption<T>>
  value?: string
  orientation?: 'vertical' | 'horizontal'
  alternate?: boolean
  badge?: ReactNode
}

export type RadioItemProps = Pick<
  Props<string>,
  'onChange' | 'name' | 'disabled'
> & {
  checked?: boolean
  id?: string
  label?: ReactNode
  value: string
  alternate?: boolean
  badge?: ReactNode
}
export const RadioItem = forwardRef(
  (
    {
      label,
      value,
      checked,
      name,
      className,
      children,
      id = `${name}_${value}`,
      onChange,
      disabled = false,
      alternate,
      badge,
    }: PropsWithChildren<PropsWithClassName<RadioItemProps>>,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    return (
      <div
        className={clsx(
          'radio flex relative min-w-0 items-center leading-none',
          className,
        )}
        key={id}
      >
        <input
          checked={checked}
          className="opacity-0 absolute peer"
          disabled={disabled}
          id={id}
          ref={ref}
          name={name}
          onChange={onChange}
          type="radio"
          value={value}
        />
        <label
          htmlFor={id}
          className={clsx(
            'flex-none cursor-pointer peer-focus:border-fv-gray-dark peer-checked:after:opacity-100 w-[26px] h-[26px] rounded-full after:rounded-full border-2 border-fv-blue after:absolute after:top-1/2 after:left-1/2 after:w-[14px] after:h-[14px] after:bg-fv-gray-dark after:opacity-0 relative after:-translate-x-1/2 after:-translate-y-1/2 duration-200 transition-[border] after:transition-all after:duration-200 peer-disabled:opacity-75 peer-disabled:cursor-not-allowed',
            {
              'cursor-not-allowed': disabled,
            },
            alternate ? 'bg-white' : 'bg-fv-blue-50',
          )}
        />
        <label
          htmlFor={id}
          className={clsx(
            ' peer-focus:after:border-fv-gray-dark peer-checked:after:opacity-100 whitespace-nowrap ml-2 peer-disabled:opacity-75 peer-disabled:cursor-not-allowed',
            {
              'cursor-not-allowed': disabled,
            },
          )}
        >
          {children}
          {label}
        </label>
        {badge && (
          <span className="bg-white border border-fv-gray rounded-xs px-1 text-sm ml-1 peer-disabled:opacity-75">
            {badge}
          </span>
        )}
      </div>
    )
  },
)

export function RadioField<T extends string>({
  className,
  disabled,
  name,
  onChange,
  options,
  value,
  orientation = 'horizontal',
  alternate,
}: Props<T>) {
  return (
    <div
      className={clsx('flex gap-x-4', className, {
        'flex-col gap-y-4': orientation === 'vertical',
      })}
    >
      {options.map(option => {
        const label = typeof option === 'string' ? option : option.label
        const optionValue = typeof option === 'string' ? option : option.value
        const optionDisabled =
          typeof option === 'string' ? false : option.disabled
        const id = `${name}_${optionValue}`
        const checked =
          typeof value === 'string' ? value === optionValue : undefined
        const badge = typeof option === 'string' ? undefined : option.badge

        return (
          <RadioItem
            key={id}
            onChange={onChange}
            id={id}
            disabled={disabled || optionDisabled}
            checked={checked}
            label={label}
            name={name}
            value={optionValue}
            alternate={alternate}
            badge={badge}
          />
        )
      })}
    </div>
  )
}
